import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Calendar, User, Clock, ArrowRight } from '../components/icons';
import { API_BASE_URL } from '../lib/api';

interface Author { firstName?: string; lastName?: string; email?: string }
interface ApiPost {
  id: string;
  title: string;
  slug: string;
  excerpt?: string | null;
  content: string;
  featuredImage?: string | null;
  category?: string | null;
  author?: Author | null;
  createdAt: string;
  publishedAt?: string | null;
}

const API_ORIGIN = API_BASE_URL.replace(/\/api\/v1\/?$/, '');

// Resolve a stored image path to something the browser can load.
const resolveImage = (src?: string | null): string | null => {
  if (!src) return null;
  if (/^https?:\/\//.test(src)) return src;         // absolute URL
  if (src.startsWith('/uploads')) return `${API_ORIGIN}${src}`; // uploaded file
  return src;                                        // /src/assets (served by Vite in dev)
};

const authorName = (a?: Author | null) =>
  a && (a.firstName || a.lastName) ? `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() : 'DentoPharma Team';

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

const readTime = (content: string) => {
  const words = content.replace(/<[^>]+>/g, ' ').split(/\s+/).filter(Boolean).length;
  return `${Math.max(1, Math.ceil(words / 200))} min read`;
};

// Lightweight Markdown → styled HTML (no external deps). Handles headings,
// bullet lists, paragraphs, bold and links — which is what our posts use.
const esc = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
const inline = (s: string) =>
  esc(s)
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" class="text-[#0D56A6] underline hover:text-[#8EC641]">$1</a>');

const mdToHtml = (md: string): string => {
  const lines = (md || '').replace(/\r\n/g, '\n').split('\n');
  const out: string[] = [];
  let inList = false;
  let para: string[] = [];
  const flushPara = () => {
    if (para.length) { out.push(`<p class="text-gray-700 leading-relaxed mb-5">${inline(para.join(' '))}</p>`); para = []; }
  };
  const closeList = () => { if (inList) { out.push('</ul>'); inList = false; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^###\s+/.test(line)) { flushPara(); closeList(); out.push(`<h3 class="text-xl font-bold text-gray-900 mt-8 mb-3">${inline(line.replace(/^###\s+/, ''))}</h3>`); }
    else if (/^##\s+/.test(line)) { flushPara(); closeList(); out.push(`<h2 class="text-2xl font-bold text-gray-900 mt-10 mb-4">${inline(line.replace(/^##\s+/, ''))}</h2>`); }
    else if (/^#\s+/.test(line)) { flushPara(); closeList(); out.push(`<h2 class="text-3xl font-extrabold text-gray-900 mt-10 mb-5">${inline(line.replace(/^#\s+/, ''))}</h2>`); }
    else if (/^[-*]\s+/.test(line)) { flushPara(); if (!inList) { out.push('<ul class="list-disc pl-6 space-y-1.5 mb-5 text-gray-700">'); inList = true; } out.push(`<li>${inline(line.replace(/^[-*]\s+/, ''))}</li>`); }
    else if (line.trim() === '') { flushPara(); closeList(); }
    else { para.push(line); }
  }
  flushPara(); closeList();
  return out.join('\n');
};

const BlogPost: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [post, setPost] = useState<ApiPost | null>(null);
  const [related, setRelated] = useState<ApiPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);

    const run = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/blog-working/blog/${id}`);
        if (!res.ok) { if (active) { setNotFound(true); setLoading(false); } return; }
        const data: ApiPost = await res.json();
        if (!active) return;
        setPost(data);

        // Pull a few other posts for the "Related Articles" strip.
        try {
          const listRes = await fetch(`${API_BASE_URL}/blog-working/blog?page=1&limit=6`);
          if (listRes.ok) {
            const list = await listRes.json();
            const others = (list.posts as ApiPost[] || []).filter(p => p.id !== data.id).slice(0, 3);
            if (active) setRelated(others);
          }
        } catch { /* related is non-critical */ }
      } catch {
        if (active) setNotFound(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    run();
    return () => { active = false; };
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <span className="inline-block animate-spin rounded-full h-10 w-10 border-b-2 border-[#0D56A6]" />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Post Not Found</h1>
          <p className="text-gray-600 mb-8">The blog post you're looking for doesn't exist.</p>
          <Link
            to="/blog"
            className="inline-flex items-center bg-[#0D56A6] hover:bg-[#0A4A8F] text-white font-bold px-6 py-3 rounded-lg transition-colors duration-300"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  const image = resolveImage(post.featuredImage);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container-custom py-4">
          <Link
            to="/blog"
            className="inline-flex items-center text-[#0D56A6] hover:text-[#0A4A8F] font-medium transition-colors duration-300"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Blog
          </Link>
        </div>
      </div>

      {/* Hero Section */}
      <section data-hero className="relative h-[60vh] min-h-[400px] overflow-hidden">
        <div className="absolute inset-0">
          {image ? (
            <img src={image} alt={post.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-[#0D56A6] to-[#0A4A8F]" />
          )}
          <div className="absolute inset-0 bg-black/50" />
        </div>
        <div className="relative z-10 h-full flex items-center justify-center pt-20 pb-10">
          <div className="text-center text-white px-4 max-w-4xl mx-auto">
            {post.category && (
              <div className="mb-4">
                <span className="bg-[#0D56A6] text-white text-sm font-bold px-4 py-2 rounded-full">
                  {post.category}
                </span>
              </div>
            )}
            <h1 className="text-3xl md:text-5xl font-bold mb-6 text-white leading-tight">
              {post.title}
            </h1>
            <div className="flex flex-wrap items-center justify-center text-gray-200 text-lg">
              <User size={20} className="mr-2" />
              <span className="mr-6">{authorName(post.author)}</span>
              <Calendar size={20} className="mr-2" />
              <span className="mr-6">{formatDate(post.publishedAt || post.createdAt)}</span>
              <Clock size={20} className="mr-2" />
              <span>{readTime(post.content)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Article Content */}
      <section className="py-16">
        <div className="container-custom max-w-4xl">
          {post.excerpt && (
            <p className="text-xl text-gray-600 leading-relaxed mb-8 pb-8 border-b border-gray-200 font-medium">{post.excerpt}</p>
          )}
          <div className="text-lg" dangerouslySetInnerHTML={{ __html: mdToHtml(post.content) }} />
        </div>
      </section>

      {/* Related Posts */}
      {related.length > 0 && (
        <section className="py-16 bg-gray-50">
          <div className="container-custom">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Related Articles
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {related.map((rp) => {
                const rImage = resolveImage(rp.featuredImage);
                return (
                  <article
                    key={rp.id}
                    className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-2 overflow-hidden group"
                  >
                    <div className="relative h-48 overflow-hidden">
                      {rImage ? (
                        <img
                          src={rImage}
                          alt={rp.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-[#0D56A6] to-[#0A4A8F]" />
                      )}
                      {rp.category && (
                        <div className="absolute top-4 left-4">
                          <span className="bg-[#0D56A6] text-white text-xs font-bold px-3 py-1 rounded-full">
                            {rp.category}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 group-hover:text-[#0D56A6] transition-colors duration-300">
                        {rp.title}
                      </h3>
                      <p className="text-gray-600 mb-4 line-clamp-3">
                        {rp.excerpt}
                      </p>
                      <Link
                        to={`/blog/${rp.id}`}
                        className="inline-flex items-center text-[#0D56A6] hover:text-[#0A4A8F] font-semibold text-sm group-hover:translate-x-1 transition-all duration-300"
                      >
                        Read More
                        <ArrowRight size={16} className="ml-1" />
                      </Link>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default BlogPost;
